import React, { useEffect, useState } from 'react';
import { SignUp, useUser, useAuth } from '@clerk/clerk-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

export default function Register() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();

  const searchSlug = searchParams.get('slug');
  const tierParam = searchParams.get('tier');
  const purchased = searchParams.get('purchased') === 'true';
  const plan = searchParams.get('plan'); // 'starter' or 'pro'

  const [linkingState, setLinkingState] = useState({
    status: 'idle', // idle, linking, success, error, not_found
    message: '',
  });

  // Persist slug across Clerk verification and nested routes
  useEffect(() => {
    if (searchSlug) {
      localStorage.setItem('tenantSlug', searchSlug);
    }
  }, [searchSlug]);

  // Handle legacy tier parameter (for backwards compatibility)
  useEffect(() => {
    if (tierParam === 'pro') {
      localStorage.setItem('pendingTierLink', 'pro');
    }
  }, [tierParam]);

  // Handle Stripe Payment Link flow: purchased=true&plan=starter/pro
  useEffect(() => {
    if (purchased && plan) {
      localStorage.setItem('pendingStripePurchase', plan);
    }
  }, [purchased, plan]);

  // After user completes signup, link their Stripe subscription
  useEffect(() => {
    const linkStripeSubscription = async () => {
      const pendingPurchase = localStorage.getItem('pendingStripePurchase');

      if (!pendingPurchase || !user || !isLoaded) {
        return;
      }

      // Only run once
      if (linkingState.status !== 'idle') {
        return;
      }

      setLinkingState({ status: 'linking', message: 'Linking your subscription...' });

      try {
        const token = await getToken();
        const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://kalibr-backend.fly.dev';
        const response = await fetch(`${API_BASE_URL}/api/billing/link-stripe-customer`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        const result = await response.json();

        if (result.status === 'success') {
          setLinkingState({
            status: 'success',
            message: `Successfully activated your ${pendingPurchase} plan!`,
          });
          localStorage.removeItem('pendingStripePurchase');

          // Redirect to dashboard after 2 seconds
          setTimeout(() => {
            navigate('/agents?welcome=true');
          }, 2000);
        } else if (result.status === 'not_found') {
          setLinkingState({
            status: 'not_found',
            message: 'Could not find your Stripe subscription. Please contact support.',
          });
          localStorage.removeItem('pendingStripePurchase');

          // Still redirect, but to billing page
          setTimeout(() => {
            navigate('/billing?link_failed=true');
          }, 3000);
        } else if (result.status === 'already_linked') {
          setLinkingState({
            status: 'success',
            message: 'Your account is already linked to a paid plan!',
          });
          localStorage.removeItem('pendingStripePurchase');

          setTimeout(() => {
            navigate('/agents');
          }, 2000);
        } else {
          setLinkingState({
            status: 'error',
            message: 'Failed to link subscription. Please contact support.',
          });
        }
      } catch (error) {
        console.error('Failed to link Stripe subscription:', error);
        setLinkingState({
          status: 'error',
          message: 'An error occurred. Please contact support.',
        });
      }
    };

    // Small delay to ensure Clerk user object is fully loaded
    const timer = setTimeout(() => {
      linkStripeSubscription();
    }, 1000);

    return () => clearTimeout(timer);
  }, [user, isLoaded, linkingState.status, navigate]);

  const slug = searchSlug || localStorage.getItem('tenantSlug');
  const slugQuery = slug ? `?slug=${slug}` : '';

  // Build destination that preserves Stripe purchase params through Clerk signup flow
  // KEY FIX: Also check localStorage so destination survives Clerk's sub-route navigation
  // (e.g. /register/verify-email-address) which clears URL search params on re-render
  const pendingPlan = plan || localStorage.getItem('pendingStripePurchase');
  let destination = '/welcome';
  if ((purchased && plan) || pendingPlan) {
    destination = `/register?purchased=true&plan=${pendingPlan}`;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-gray-900">Create Account</h2>
          <p className="mt-2 text-sm text-gray-600">
            Start monitoring your LLM applications
          </p>
        </div>

        {/* Show linking status message if we're in the process of linking */}
        {linkingState.status !== 'idle' && (
          <div
            className={`mb-4 p-4 rounded-lg border ${
              linkingState.status === 'linking'
                ? 'bg-blue-50 border-blue-200'
                : linkingState.status === 'success'
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
            }`}
          >
            <div className="flex items-center gap-3">
              {linkingState.status === 'linking' && (
                <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              )}
              {linkingState.status === 'success' && (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
              {(linkingState.status === 'error' || linkingState.status === 'not_found') && (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
              <p
                className={`text-sm ${
                  linkingState.status === 'linking'
                    ? 'text-blue-800'
                    : linkingState.status === 'success'
                    ? 'text-green-800'
                    : 'text-red-800'
                }`}
              >
                {linkingState.message}
              </p>
            </div>
          </div>
        )}

        <SignUp
          routing="path"
          path="/register"
          signInUrl={`/login${slugQuery}`}
          afterSignInUrl={destination}
          afterSignUpUrl={destination}
        />
      </div>
    </div>
  );
}
